import type { IconNames } from "@/v2-ui/icon/icon.types"
import type { ColumnFilterOption } from "@/v2-backoffice-client/components/table"
import type { TenantUserActivationFragment } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.fragment"
import type { TenantActivationResendMutation } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.resend.mutation"

// The GraphQL ActivationStatus / ActivationOutcome enums, as they reach the
// client (string values). Kept as string literals so call sites don't have
// to import the generated enum object at runtime — same pattern as
// userContact.constants. Each constant is typed as the enum's value-literal
// union (`${…}`): string-enum members are assignable to their literal
// types, so enum-typed fields compare and index against these with no
// casts while the zero-runtime-import contract holds.
export type ActivationStatusValue = TenantUserActivationFragment["Status"]
type ActivationStatusLiteral = `${ActivationStatusValue}`
export type ActivationOutcomeValue =
  TenantActivationResendMutation["resendUserActivation"]["Outcome"]
type ActivationOutcomeLiteral = `${ActivationOutcomeValue}`

// One user's result from the last wave/resend run in a dialog session —
// shared by the dialog (which accumulates them) and the row/wave-button
// components (which produce them).
export type ActivationRunOutcome = {
  outcome: ActivationOutcomeValue
  error?: string | null
}

export const ACTIVATION_STATUS_NOT_MIGRATED: ActivationStatusLiteral =
  "NOT_MIGRATED"
const ACTIVATION_STATUS_PENDING: ActivationStatusLiteral = "PENDING"
const ACTIVATION_STATUS_EXPIRED: ActivationStatusLiteral = "EXPIRED"
export const ACTIVATION_STATUS_ACTIVATED: ActivationStatusLiteral = "ACTIVATED"
const ACTIVATION_STATUS_MIGRATED_ELSEWHERE: ActivationStatusLiteral =
  "MIGRATED_ELSEWHERE"
const ACTIVATION_STATUS_BLOCKED: ActivationStatusLiteral = "BLOCKED"

type ActivationStatusStyle = {
  label: string
  iconColor: string
  iconSrc: IconNames
  description: string
}

// One label + icon per status — the single source for the badge, the
// members-table grouping/export values, and the filter options. Keyed
// exhaustively: adding an ActivationStatus enum value without a style is a
// compile error, not a silent "Unknown" badge.
const ACTIVATION_STATUS_STYLES: Record<
  ActivationStatusLiteral,
  ActivationStatusStyle
> = {
  [ACTIVATION_STATUS_NOT_MIGRATED]: {
    label: "Not migrated",
    iconColor: "text-muted-foreground",
    iconSrc: "minus-circle",
    description: "Still on the old login; no activation has been issued.",
  },
  [ACTIVATION_STATUS_PENDING]: {
    label: "Pending",
    iconColor: "text-amber-500",
    iconSrc: "hourglass",
    description:
      "Activation email sent; the user hasn't completed it yet. They keep signing in the old way until they do.",
  },
  [ACTIVATION_STATUS_EXPIRED]: {
    label: "Expired",
    iconColor: "text-destructive",
    iconSrc: "alarm-exclamation",
    description:
      "The activation link expired unused. Resend issues a fresh link (the old one stays dead).",
  },
  [ACTIVATION_STATUS_ACTIVATED]: {
    label: "Activated",
    iconColor: "text-green-500",
    iconSrc: "check-circle",
    description: "The user completed activation and signs in on Fusion login.",
  },
  [ACTIVATION_STATUS_MIGRATED_ELSEWHERE]: {
    label: "Migrated directly",
    iconColor: "text-green-500",
    iconSrc: "user-check",
    description:
      "The user already has a Fusion login account without an activation here — another team's wave, staff SSO, or the dev backfill.",
  },
  [ACTIVATION_STATUS_BLOCKED]: {
    label: "Blocked",
    iconColor: "text-amber-500",
    iconSrc: "lock",
    description: "Blocked users are skipped by migration waves.",
  },
}

const DEFAULT_ACTIVATION_STATUS_STYLE: ActivationStatusStyle = {
  label: "Unknown",
  iconColor: "text-muted-foreground",
  iconSrc: "question",
  description: "",
}

export function getActivationStatusStyle(
  status: ActivationStatusValue | undefined,
): ActivationStatusStyle {
  if (!status) return DEFAULT_ACTIVATION_STATUS_STYLE
  // The ?? guards the runtime case the types can't: a server that ships a
  // NEW status value before this bundle regenerates its types.
  return ACTIVATION_STATUS_STYLES[status] ?? DEFAULT_ACTIVATION_STATUS_STYLE
}

// Grouping / export / client-side-filter value for a status. Empty for
// members the activations query hasn't resolved (yet).
export function getActivationStatusLabel(
  status: ActivationStatusValue | undefined,
): string {
  if (!status) return ""
  return getActivationStatusStyle(status).label
}

// Options for the client-side-filtered members table: its multi-select
// filter compares against the column's getValue — the label.
export const ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS: ColumnFilterOption[] =
  Object.values(ACTIVATION_STATUS_STYLES).map((style) => ({
    value: style.label,
    label: style.label,
  }))

// Options for the server-filtered Users board: values are the enum values
// the MigrationStatus filter registry entry (backoffice-api) compares
// against — its SQL twin of the status derivation.
export const ACTIVATION_STATUS_FILTER_OPTIONS: ColumnFilterOption[] =
  Object.entries(ACTIVATION_STATUS_STYLES).map(([value, style]) => ({
    value,
    label: style.label,
  }))

// A per-member send helps in every state where the member still needs a
// working link: never migrated (their first send), or an open activation
// that wasn't completed (a fresh link). Everything else is either done or
// needs an unblock first. The send runs the wave's single-user path, so
// fresh sends and renewals share one action.
export function canSendActivation(
  status: ActivationStatusValue | undefined,
): boolean {
  return (
    status === ACTIVATION_STATUS_NOT_MIGRATED ||
    status === ACTIVATION_STATUS_PENDING ||
    status === ACTIVATION_STATUS_EXPIRED
  )
}

// What a WAVE will actually email: first sends and expired-link renewals.
// Pending members are skipped by waves (their link still works; a renewal
// would kill it) — only the explicit per-member send reaches them, which
// is what canSendActivation governs.
export function waveWillEmail(
  status: ActivationStatusValue | undefined,
): boolean {
  return (
    status === ACTIVATION_STATUS_NOT_MIGRATED ||
    status === ACTIVATION_STATUS_EXPIRED
  )
}

// The onboarding framing an activation delivers (GraphQL enum
// ActivationExperience, decided automatically at wave time from v2-usage
// evidence and frozen on the row): MIGRATION = "your sign-in was upgraded"
// for demonstrated v2 users; NEW_APP = "welcome to the brand-new app" for
// v1-only users, who never hear the word migration.
export type ActivationExperienceValue = NonNullable<
  TenantUserActivationFragment["Experience"]
>
type ActivationExperienceLiteral = `${ActivationExperienceValue}`

const ACTIVATION_EXPERIENCE_LABELS: Record<
  ActivationExperienceLiteral,
  string
> = {
  MIGRATION: "Migration",
  NEW_APP: "New app",
}

const ACTIVATION_EXPERIENCE_DESCRIPTIONS: Record<
  ActivationExperienceLiteral,
  string
> = {
  MIGRATION:
    'Active v2 user — their journey says "your sign-in was upgraded, same account".',
  NEW_APP:
    'v1-only user — their journey says "welcome to the brand-new Placepoint" and never mentions migration.',
}

/** Short label for the experience tag; empty when no activation exists. */
export function getActivationExperienceLabel(
  experience: ActivationExperienceValue | null | undefined,
): string {
  if (!experience) return ""
  return ACTIVATION_EXPERIENCE_LABELS[experience] ?? ""
}

/** Tooltip for the experience tag; empty when no activation exists. */
export function getActivationExperienceDescription(
  experience: ActivationExperienceValue | null | undefined,
): string {
  if (!experience) return ""
  return ACTIVATION_EXPERIENCE_DESCRIPTIONS[experience] ?? ""
}

type ActivationOutcomeStyle = {
  label: string
  iconColor: string
  iconSrc: IconNames
}

// Per-user wave/resend outcomes, rendered in the dialog's result column.
// Exhaustive over ActivationOutcome — a new outcome value fails the build
// until it gets a style.
const ACTIVATION_OUTCOME_STYLES: Record<
  ActivationOutcomeLiteral,
  ActivationOutcomeStyle
> = {
  SENT: {
    label: "Activation sent",
    iconColor: "text-green-500",
    iconSrc: "paper-plane",
  },
  RENEWED: {
    label: "Renewed + re-sent",
    iconColor: "text-green-500",
    iconSrc: "arrows-repeat",
  },
  ALREADY_ACTIVATED: {
    label: "Already activated",
    iconColor: "text-muted-foreground",
    iconSrc: "check-circle",
  },
  // Multi-team member: an earlier selected team already emailed them in
  // this batch, so this team skipped them — one email per user per batch.
  COVERED_BY_BATCH: {
    label: "Emailed via another selected team",
    iconColor: "text-muted-foreground",
    iconSrc: "check-circle",
  },
  // Already invited by an earlier run: their link is still valid, so the
  // wave left it alone. The row's send button is the explicit resend.
  SKIPPED_PENDING: {
    label: "Already invited — link still valid",
    iconColor: "text-muted-foreground",
    iconSrc: "check-circle",
  },
  SKIPPED_BLOCKED: {
    label: "Skipped (blocked)",
    iconColor: "text-amber-500",
    iconSrc: "lock",
  },
  SKIPPED_NO_EMAIL: {
    label: "Skipped (no email)",
    iconColor: "text-amber-500",
    iconSrc: "envelope-ban",
  },
  LINK_REFUSED: {
    label: "Link refused",
    iconColor: "text-destructive",
    iconSrc: "alarm-exclamation",
  },
  FAILED: {
    label: "Failed",
    iconColor: "text-destructive",
    iconSrc: "alarm-exclamation",
  },
}

const DEFAULT_ACTIVATION_OUTCOME_STYLE: ActivationOutcomeStyle = {
  label: "Unknown",
  iconColor: "text-muted-foreground",
  iconSrc: "question",
}

export function getActivationOutcomeStyle(
  outcome: ActivationOutcomeValue,
): ActivationOutcomeStyle {
  return ACTIVATION_OUTCOME_STYLES[outcome] ?? DEFAULT_ACTIVATION_OUTCOME_STYLE
}
